import { ChevronDown } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu'
import { cn } from '@/lib/utils'

export interface PresetOption {
  id: string
  /** What the option is, on one line: the line you choose by. */
  title: string
  /** The rest of it, for telling two of them apart. */
  detail?: string
}

/**
 * An offer, beside a field's label, to fill the field in from something the ERP already holds.
 *
 * It sits in the label row rather than in the field because it is not the field: what the form
 * holds afterwards is an ordinary typed value, which the user is free to correct. Picking from
 * here saves the typing and nothing else — no link to the thing picked is kept, and the field
 * stays editable in exactly the way it was before.
 *
 * Greyed out rather than hidden when there is nothing to offer. A customer with no stored
 * addresses and a customer we have not matched yet are both ordinary states, and a control that
 * comes and goes between them would read as a glitch.
 */
export function UseSaved({
  label,
  options,
  loading = false,
  emptyReason,
  onPick,
}: {
  /** Names the field this fills, for a screen reader and for the empty state's title. */
  label: string
  options: PresetOption[]
  loading?: boolean
  /** Why there is nothing to offer, shown on hover when the offer is greyed out. */
  emptyReason?: string
  onPick: (id: string) => void
}) {
  const unavailable = loading || options.length === 0

  const TRIGGER =
    'flex items-center gap-0.5 text-[10px] font-semibold uppercase tracking-wide transition-colors'

  if (unavailable) {
    return (
      <span
        className={cn(TRIGGER, 'cursor-default text-gray-300')}
        title={loading ? undefined : emptyReason}
        aria-disabled
      >
        Use saved
        <ChevronDown className="h-3 w-3" />
      </span>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`Use a saved ${label}`}
          className={cn(TRIGGER, 'text-brand-500 hover:text-brand-700')}
        >
          Use saved
          <ChevronDown className="h-3 w-3" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="max-h-72 w-72 overflow-y-auto border-sheet-border bg-white"
      >
        {options.map((option) => (
          <DropdownMenuItem
            key={option.id}
            onSelect={() => onPick(option.id)}
            className="cursor-pointer flex-col items-start gap-0.5 focus:bg-gray-100"
          >
            <span className="text-sm text-gray-900">{option.title}</span>
            {option.detail && <span className="text-xs text-gray-500">{option.detail}</span>}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
